import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { GraduationCap, FlaskConical, LineChart, Binary, Database, Cpu } from "lucide-react";

const EducationUniversity = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Random Number Generator for Universities & Colleges
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Professional-grade cryptographic RNG for academic research, statistical computing, machine learning, 
            scientific simulations, and graduate-level coursework. CSPRNG trusted by researchers worldwide.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <FlaskConical className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Academic Research</h3>
              <p className="text-muted-foreground">
                Cryptographically secure randomness for clinical trials, experimental designs, Monte Carlo methods, and peer-reviewed research.
              </p>
            </Card>
            <Card className="p-6">
              <LineChart className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Statistical Computing</h3>
              <p className="text-muted-foreground">
                Support R, Python, MATLAB workflows with true random seeds for bootstrapping, permutation tests, and Bayesian inference.
              </p>
            </Card>
            <Card className="p-6">
              <Cpu className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Machine Learning & AI</h3>
              <p className="text-muted-foreground">
                Generate random initialization, data augmentation, cross-validation splits, and stochastic gradient descent seeds.
              </p>
            </Card>
          </div>
        </section>

        {/* Academic Disciplines */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">University Research Applications</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <LineChart className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Statistics & Biostatistics</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Randomized Controlled Trials (RCTs):</strong> Blind assignment for clinical research, drug trials, intervention studies</li>
                <li>• <strong>Bootstrap Resampling:</strong> Generate 10,000+ bootstrap samples for confidence interval estimation</li>
                <li>• <strong>Permutation Tests:</strong> Non-parametric hypothesis testing with random label shuffling</li>
                <li>• <strong>MCMC (Markov Chain Monte Carlo):</strong> Bayesian inference, Gibbs sampling, Metropolis-Hastings algorithms</li>
                <li>• <strong>Cross-Validation:</strong> K-fold CV with random data splits for model validation</li>
                <li>• <strong>Missing Data Imputation:</strong> Multiple imputation with random draws from predictive distributions</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Binary className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Computer Science & Engineering</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Cryptography Research:</strong> CSPRNG analysis, entropy sources, key generation algorithms</li>
                <li>• <strong>Algorithm Randomization:</strong> Randomized quicksort, Las Vegas & Monte Carlo algorithms</li>
                <li>• <strong>Network Simulations:</strong> Random packet loss, latency jitter, node failure modeling</li>
                <li>• <strong>Load Balancing:</strong> Consistent hashing, random routing, distributed systems research</li>
                <li>• <strong>Optimization:</strong> Simulated annealing, genetic algorithms, random search heuristics</li>
                <li>• <strong>Blockchain & Security:</strong> Nonce generation, proof-of-work simulations, consensus protocols</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Cpu className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Machine Learning & Data Science</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Neural Network Training:</strong> Random weight initialization (Xavier, He initialization)</li>
                <li>• <strong>Data Augmentation:</strong> Random crops, flips, rotations for image classification robustness</li>
                <li>• <strong>Dropout Regularization:</strong> Randomly drop neurons during training to prevent overfitting</li>
                <li>• <strong>Hyperparameter Tuning:</strong> Random search over parameter space (vs exhaustive grid search)</li>
                <li>• <strong>Train/Test Splits:</strong> Stratified random sampling for unbiased model evaluation</li>
                <li>• <strong>Ensemble Methods:</strong> Random forests, bagging with random feature subsets</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <FlaskConical className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Natural & Social Sciences</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Physics Simulations:</strong> Quantum mechanics, particle collisions, molecular dynamics (MD)</li>
                <li>• <strong>Economics & Finance:</strong> Monte Carlo option pricing, risk analysis, stochastic processes</li>
                <li>• <strong>Psychology:</strong> Randomized stimulus presentation, counterbalancing, participant assignment</li>
                <li>• <strong>Ecology:</strong> Spatial point processes, population dynamics, metapopulation models</li>
                <li>• <strong>Chemistry:</strong> Kinetic Monte Carlo, molecular sampling, chemical reaction networks</li>
                <li>• <strong>Survey Research:</strong> Probability sampling, quota sampling, stratified random samples</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Graduate Course Support */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">Graduate-Level Course Support</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-xl mb-4">Statistics & Mathematics</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• <strong>STAT 601:</strong> Probability Theory – demonstrate Law of Large Numbers, CLT empirically</li>
                  <li>• <strong>STAT 615:</strong> Statistical Computing – implement MCMC, EM algorithm, bootstrap methods</li>
                  <li>• <strong>BIOS 741:</strong> Clinical Trials Design – randomization schemes, blocking, stratification</li>
                  <li>• <strong>MATH 555:</strong> Stochastic Processes – random walks, Brownian motion, Markov chains</li>
                  <li>• <strong>ECON 712:</strong> Econometrics – instrumental variables, randomized experiments, DID</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-xl mb-4">Computer Science & Engineering</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• <strong>CS 6140:</strong> Machine Learning – data splits, random initialization, cross-validation</li>
                  <li>• <strong>CS 5830:</strong> Cryptography – PRNG vs CSPRNG, entropy analysis, randomness testing</li>
                  <li>• <strong>CS 6210:</strong> Advanced Algorithms – randomized algorithms, probabilistic analysis</li>
                  <li>• <strong>ECE 6254:</strong> Statistical ML – Monte Carlo methods, variational inference, sampling</li>
                  <li>• <strong>CS 7641:</strong> Deep Learning – stochastic optimization, random weight init, dropout</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Research Methodology */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Academic Research Standards</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-3">Reproducible Research</h3>
              <p className="text-muted-foreground mb-4">
                Use our RNG with fixed seeds for reproducible results in journal submissions:
              </p>
              <div className="bg-muted/30 p-4 rounded text-sm font-mono">
                # Python example<br/>
                import secrets<br/>
                seed = secrets.randbits(128)<br/>
                random.seed(seed)  # Document seed
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-3">Publication Quality</h3>
              <p className="text-muted-foreground mb-4">
                Our CSPRNG meets standards for peer-reviewed research:
              </p>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li>✓ Passes NIST Statistical Test Suite</li>
                <li>✓ Cryptographic entropy sources</li>
                <li>✓ Suitable for RCTs and FDA submissions</li>
                <li>✓ Documented in methodology sections</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Integration Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Integration with Research Tools</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="p-6">
              <Database className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">R & RStudio</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Export random seeds for set.seed(), sample(), and statistical packages
              </p>
              <code className="text-xs bg-muted/30 p-2 rounded block">
                set.seed(42)<br/>
                sample(1:100, 30)
              </code>
            </Card>
            <Card className="p-6">
              <Binary className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-lg font-semibold mb-2">Python / NumPy</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Compatible with NumPy random, secrets module, and sklearn
              </p>
              <code className="text-xs bg-muted/30 p-2 rounded block">
                np.random.seed(42)<br/>
                np.random.randint(0,100)
              </code>
            </Card>
            <Card className="p-6">
              <LineChart className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">MATLAB / SAS</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Generate seeds for rng(), randn(), and statistical procedures
              </p>
              <code className="text-xs bg-muted/30 p-2 rounded block">
                rng(42, 'twister')<br/>
                randsample(100, 30)
              </code>
            </Card>
          </div>
        </section>

        {/* Dissertation & Thesis Support */}
        <section className="mb-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">Dissertation & Thesis Research</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-muted-foreground text-center mb-6">
              PhD and Master's students: Document your randomization procedures with our cryptographic RNG
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Methods Section Language</h4>
                <p className="text-sm text-muted-foreground italic bg-muted/20 p-4 rounded">
                  "Participants were randomly assigned to treatment conditions using a cryptographically secure 
                  pseudo-random number generator (Web Crypto API, crypto.getRandomValues()) to ensure unbiased allocation..."
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">IRB Requirements</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Auditable randomization process</li>
                  <li>✓ Secure, unpredictable assignment</li>
                  <li>✓ No selection bias</li>
                  <li>✓ Compliant with research ethics boards</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Tools */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-6">Research-Grade Tools</h2>
          <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <Link to="/">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <Binary className="w-10 h-10 text-primary mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Bulk Generator</h4>
                <p className="text-xs text-muted-foreground">10,000+ numbers</p>
              </Card>
            </Link>
            <Link to="/numbers">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <LineChart className="w-10 h-10 text-accent mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Distributions</h4>
                <p className="text-xs text-muted-foreground">Normal, uniform, etc.</p>
              </Card>
            </Link>
            <Link to="/statistics">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <FlaskConical className="w-10 h-10 text-primary mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Quality Tests</h4>
                <p className="text-xs text-muted-foreground">Chi-square, runs test</p>
              </Card>
            </Link>
            <Link to="/lists">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <Database className="w-10 h-10 text-accent mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Randomizer</h4>
                <p className="text-xs text-muted-foreground">Lists & sampling</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Trusted by Researchers Worldwide</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            Free, unlimited access for academic research. No registration required. CSPRNG-quality randomness for publication.
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Start Research Project
            </Button>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default EducationUniversity;